/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  Button,
  Description,
  Input,
  Label,
  Sidebar,
  Spacer,
  Switch,
  Text,
  theme,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { RouteParams, Sla, SLAUnitType } from "~/models";
import {
  generateActionSuccessText,
  displaySlaType,
  useAxios,
  displayServiceHours,
  displaySlaUnitType,
} from "~/tools";

type EditProductSlaProps = {
  refetch: () => void;
  sla: Sla;
  val: string;
  comm: string;
  productId: string;
};

type Values = {
  comment: string;
  value: string;
};

function EditProductSla({
  refetch,
  sla,
  val,
  comm,
  productId,
}: EditProductSlaProps) {
  const { customerId } = useParams<RouteParams>();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isDefaultValue, setIsDefaultValue] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();
  const { handleSubmit, errors, register } = useForm();

  const onSubmit = async (values: Values) => {
    const organizedValues = {
      comment: values.comment,
      value: Number(values.value),
    };
    try {
      await Axios().put(
        `/customers/${customerId}/products/${productId}/slas/${sla.id}`,
        organizedValues
      );
      showSuccess(generateActionSuccessText(`Product Sla`)()(`added`)());
      refetch();
    } catch (e) {
      showError(e.response);
    } finally {
      setIsSidebarOpen(false);
    }
  };

  React.useEffect(() => {
    setIsDefaultValue(sla?.range?.default === val);
  }, [sla?.range?.default, val]);

  const clean = () => {
    refetch && refetch();
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        type="button"
        size="sm"
        severity="medium"
        icon="edit"
      >
        Edit SLA
      </Button>
      <Sidebar
        title="Edit SLA"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <Text tag="div" color={theme.color.text.text02}>
          Name:
        </Text>
        <Text weight="medium">{sla?.name}</Text>
        {sla?.description && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Info:
            </Text>
            <Text weight="medium">{sla?.description}</Text>
          </>
        )}
        {sla?.priority && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Priority level:
            </Text>
            <Text weight="medium">{sla?.priority}</Text>
          </>
        )}
        <Text tag="div" color={theme.color.text.text02}>
          Support service hours:
        </Text>
        <Text weight="medium">{displayServiceHours(sla?.supportHours)}</Text>
        {sla?.slaType && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Type:
            </Text>
            <Text weight="medium">{displaySlaType(sla?.slaType)}</Text>
          </>
        )}
        {sla?.unitType && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Unit Type:
            </Text>
            <Text weight="medium">{displaySlaUnitType(sla?.unitType)}</Text>
          </>
        )}
        {sla.unitType === SLAUnitType.VALUE_RANGE && (
          <Fieldset>
            <Label name="Value" />
            <Text weight="medium" size="sm">
              Value has to be within range Min: {sla?.range?.min} Max:{" "}
              {sla?.range?.max} and Unit: {sla?.range?.unit}
            </Text>
            <Switch
              position="right"
              labelText="Use default value"
              checked={isDefaultValue}
              onChange={() => setIsDefaultValue(!isDefaultValue)}
            />
            <Spacer height="0.5rem" />
            {isDefaultValue ? (
              <div key="default">
                <input
                  type="hidden"
                  name="value"
                  disabled
                  value={sla?.range?.default}
                  ref={register}
                />
                <Spacer height="1.1rem" />
                <Text size="md" tag="span" color={theme.colors.success}>
                  {sla?.range?.default}
                </Text>
              </div>
            ) : (
              <div key="value">
                <Input
                  type="number"
                  name="value"
                  defaultValue={val}
                  status={errors.value && "danger"}
                  placeholder="value"
                  ref={register({
                    required: true,
                    min: sla?.range?.min,
                    max: sla?.range?.max,
                  })}
                />
              </div>
            )}
            {errors.value && (
              <Description>
                Value has to be within range Min: {sla?.range?.min} Max:{" "}
                {sla?.range?.max}
              </Description>
            )}
          </Fieldset>
        )}
        <Fieldset>
          <Label name="Comment" />
          <Input
            type="text"
            name="comment"
            defaultValue={comm}
            status={errors.comment && "danger"}
            placeholder="Type additional comment"
            ref={register}
          />
          {errors.comment && <Description>Comment is required.</Description>}
        </Fieldset>
        <FlexContainer>
          <Button
            css={{ marginRight: "auto" }}
            onClick={handleSubmit(onSubmit)}
            type="submit"
          >
            Save
          </Button>
          <Button
            severity="medium"
            type="button"
            onClick={() => {
              setIsSidebarOpen(!isSidebarOpen);
              clean();
            }}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}

export { EditProductSla };
